import { useEffect, useState } from 'react'
import { Grid, useTheme, CircularProgress, Button } from '@mui/material'
import { ParamsType, PreRegistrationCustomProcess } from '../utils/sharedTypes'
import { BsCheck } from 'react-icons/bs'
import { client } from '../utils/client'
import Bugsnag from '@bugsnag/js'

const DocusignSuccess = () => {
  const theme = useTheme()
  const [params, setParams] = useState<ParamsType>({})
  const [isLoading, setIsLoading] = useState(true)
  const [signed, setSigned] = useState<boolean | null>(null)
  const [retry, setRetry] = useState(3000)

  useEffect(() => {
    const searchObj = window.location.search ? Object.fromEntries(new URLSearchParams(window.location.search)) : {}
    setParams(searchObj)
  }, [])

  const signDocusignPrcp = async (id: string, registrationGlobalId?: string) => {
    // The CRM binds a public process to its registration through this (unguessable) global_id.
    const resp = await client.post(`/registration/pre_registration_custom_processes/${id}/sign_docusign/`, undefined, {
      params: registrationGlobalId ? { registration_global_id: registrationGlobalId } : undefined,
    })
    const prcp = resp.data as PreRegistrationCustomProcess
    if (prcp.validated) {
      setSigned(true)
      window.parent.postMessage('DOCUSIGN_SIGNED_REFRESH')
    } else if (retry < 20000) {
      setTimeout(() => setRetry((prevState) => prevState + prevState), retry)
    } else {
      setSigned(false)
    }
  }

  const signDocusignRegistration = async (globalId: string, force = false) => {
    const endpoint =
      params.flow === 'gocardless'
        ? `/registration/${globalId}/sign_gocardless_docusign/`
        : `/registration/${globalId}/sign_docusign/`
    const resp = await client.post(endpoint, undefined, {
      params: force ? { force: '1' } : undefined,
    })
    const data = resp.data as { signed: boolean; url: string | null }
    if (data.signed) {
      setSigned(true)
      window.parent.postMessage('DOCUSIGN_SIGNED_REFRESH')
    } else if (retry < 20000) {
      setTimeout(() => setRetry((prevState) => prevState + prevState), retry)
    } else {
      setSigned(false)
    }
  }

  const sign = (force = false) => {
    if (params.registration_global_id) {
      return signDocusignRegistration(params.registration_global_id, force)
    }
    if (params.prcp_id) {
      return signDocusignPrcp(params.prcp_id, params.prcp_registration_global_id)
    }
    return Promise.resolve()
  }

  useEffect(() => {
    if (params.registration_global_id || params.prcp_id) {
      // First attempt after redirect bypasses the server-side DocuSign status cache.
      sign(retry === 3000).catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, retry])

  useEffect(() => {
    if (signed !== null) {
      setIsLoading(false)
    }
  }, [signed])

  if (isLoading) {
    return (
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        style={{
          display: 'flex',
          height: '100vh',
        }}
      >
        <Grid
          style={{
            flex: 1,
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      style={{
        display: 'flex',
        height: '100vh',
      }}
    >
      {!signed ? (
        <>
          <div style={{ textAlign: 'center', marginBottom: '10px' }}>
            Un problème est survenu lors de la signature, veuillez cliquer ci-dessous.
          </div>
          <Button
            variant="contained"
            sx={{ marginTop: '5px' }}
            onClick={() => {
              sign().catch(console.error)
            }}
          >
            Rafraîchir
          </Button>
        </>
      ) : (
        <>
          <Grid
            item
            sx={{
              background: theme.palette.success.main,
              height: 80,
              width: 80,
              borderRadius: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <BsCheck size="40px" style={{ color: 'white' }} />
          </Grid>
          Documents signés avec succès
        </>
      )}
    </Grid>
  )
}

export default DocusignSuccess
